import type { QuotationLineItem } from "@/mocks/quotationsData";
import { taxOptions } from "@/mocks/quotationsData";

type Props = {
  items: QuotationLineItem[];
  onChange: (items: QuotationLineItem[]) => void;
};

export function QuotationLineItems({ items, onChange }: Props) {
  const updateItem = (id: number, field: keyof QuotationLineItem, value: string | number) => {
    onChange(items.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  };

  const removeItem = (id: number) => {
    onChange(items.filter((item) => item.id !== id));
  };

  const addItem = () => {
    const maxId = items.reduce((max, i) => Math.max(max, i.id), 0);
    const newItem: QuotationLineItem = {
      id: maxId + 1,
      description: "",
      qty: 1,
      unit: "Unit",
      rate: 0,
      taxPercent: 0,
    };
    onChange([...items, newItem]);
  };

  const computeAmount = (item: QuotationLineItem) => {
    return item.qty * item.rate * (1 + item.taxPercent / 100);
  };

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-xs uppercase text-gray-500 tracking-wider">
              <th className="text-center px-3 py-2.5 font-semibold w-10">#</th>
              <th className="text-left px-3 py-2.5 font-semibold">Description</th>
              <th className="text-center px-3 py-2.5 font-semibold w-16">Qty</th>
              <th className="text-left px-3 py-2.5 font-semibold w-24">Unit</th>
              <th className="text-right px-3 py-2.5 font-semibold w-28">Rate (₹)</th>
              <th className="text-center px-3 py-2.5 font-semibold w-20">Tax %</th>
              <th className="text-right px-3 py-2.5 font-semibold w-28">Amount (₹)</th>
              <th className="text-center px-3 py-2.5 font-semibold w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((item, idx) => (
              <tr key={item.id} className="group hover:bg-gray-50/50 transition-colors">
                <td className="px-3 py-2 text-center text-gray-400 text-xs">{idx + 1}</td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => updateItem(item.id, "description", e.target.value)}
                    placeholder="Enter description..."
                    className="w-full px-2 py-1.5 text-sm border border-transparent hover:border-gray-200 focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 rounded-md bg-transparent focus:bg-white outline-none transition-all"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    value={item.qty}
                    onChange={(e) => updateItem(item.id, "qty", Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-full px-2 py-1.5 text-sm text-center border border-transparent hover:border-gray-200 focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 rounded-md bg-transparent focus:bg-white outline-none transition-all"
                    min={1}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={item.unit}
                    onChange={(e) => updateItem(item.id, "unit", e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-transparent hover:border-gray-200 focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 rounded-md bg-transparent focus:bg-white outline-none transition-all"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    value={item.rate}
                    onChange={(e) => updateItem(item.id, "rate", Math.max(0, parseFloat(e.target.value) || 0))}
                    className="w-full px-2 py-1.5 text-sm text-right border border-transparent hover:border-gray-200 focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 rounded-md bg-transparent focus:bg-white outline-none transition-all"
                    min={0}
                  />
                </td>
                <td className="px-3 py-2">
                  <select
                    value={item.taxPercent}
                    onChange={(e) => updateItem(item.id, "taxPercent", parseInt(e.target.value))}
                    className="w-full px-2 py-1.5 text-sm text-center border border-transparent hover:border-gray-200 focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 rounded-md bg-transparent focus:bg-white outline-none transition-all cursor-pointer"
                  >
                    {taxOptions.map((t) => (
                      <option key={t} value={t}>{t}%</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2 text-right text-sm font-medium text-gray-900 whitespace-nowrap">
                  ₹{computeAmount(item).toLocaleString("en-IN")}
                </td>
                <td className="px-3 py-2 text-center">
                  <button
                    onClick={() => removeItem(item.id)}
                    className="w-7 h-7 rounded-lg hover:bg-red-50 flex items-center justify-center text-gray-300 hover:text-red-500 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                    title="Remove item"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add item row */}
      <button
        onClick={addItem}
        className="w-full mt-2 border-2 border-dashed border-gray-200 rounded-lg p-3 text-center text-sm text-indigo-600 font-medium hover:bg-indigo-50/50 hover:border-indigo-200 transition-all cursor-pointer"
      >
        <i className="ri-add-line mr-1" />
        Add Item
      </button>
    </div>
  );
}